import { useCallback, useMemo, useState } from 'react';
import { FieldType } from 'src/services';
import { dataFieldHasTypes } from 'src/utils/data-field-has-types';
import { useAppSelector } from 'src/hooks';
import { DataFieldToDisplay } from 'src/components-bl/EmailMarketing/constants/data-fields-to-display';
import { CampaignDraft } from 'src/components-bl/EmailMarketing/types/campaign-draft';
import {
  ProductCardConfig,
  WidgetMaker,
} from 'src/components-bl/EmailMarketing/types/widget-maker';
import { defaultWidgetMakerConfig } from 'src/components-bl/EmailMarketing/constants/default-widget-maker-config';

interface UseCatalogFieldsProps {
  onChange: (campaign: CampaignDraft) => void;
  widgetMaker?: Partial<WidgetMaker>;
}

interface UseCatalogFieldsReturn {
  dataFieldsToDisplay: DataFieldToDisplay[];
  dataFieldsDropdownOptions: DataFieldToDisplay[];
  onMoveCard: (index: number, direction: 'up' | 'down') => void;
  handleSelectChange: (index: number, item: DataFieldToDisplay) => void;
  handleStyleChange: (field: string, value: any, index: number) => void;
  onRemove: (index: number) => void;
  onAdd: VoidFunction;
  isEmpty: boolean;
}

export const useCatalogFields = ({
  onChange,
  widgetMaker,
}: UseCatalogFieldsProps): UseCatalogFieldsReturn => {
  const dataFields = useAppSelector(state =>
    state.dataFields?.dataFields?.filter(
      dataField => dataField.fieldType === FieldType.CatalogField && dataFieldHasTypes(dataField)
    )
  );

  const [productCard, setProductCard] = useState<ProductCardConfig[]>(
    widgetMaker?.productCard ?? []
  );

  const dataFieldsToDisplay = useMemo(() => {
    return productCard.map(card => {
      const fields = dataFields.find(field => field.name === card.field);
      return {
        id: fields?.id,
        value: card.field,
        position: card.position,
      };
    });
  }, [productCard, dataFields]);

  const updateProductCards = useCallback(
    (updatedCards: ProductCardConfig[]) => {
      const updated = updatedCards.map((card, index) => ({
        ...card,
        position: index,
      }));
      setProductCard(updated);
      onChange({
        widgetMaker: {
          ...widgetMaker,
          productCard: updated,
        },
      });
    },
    [onChange, widgetMaker]
  );

  const handleSelectChange = useCallback(
    (index: number, item: DataFieldToDisplay) => {
      const updated = [...productCard];
      updated[index] = {
        ...updated[index],
        field: item.value,
      };
      updateProductCards(updated);
    },
    [productCard, updateProductCards]
  );

  const handleStyleChange = useCallback(
    (field: string, value: any, index: number) => {
      const updated = [...productCard];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      updateProductCards(updated);
    },
    [productCard, updateProductCards]
  );

  const dataFieldsDropdownOptions: DataFieldToDisplay[] = useMemo(() => {
    const selectedFields = new Set(productCard.map(card => card.field));

    return dataFields
      .filter(field => !selectedFields.has(field.name))
      .map(field => ({
        id: field.id,
        value: field.name,
        position: dataFieldsToDisplay.length,
      }));
  }, [dataFields, dataFieldsToDisplay]);

  const onRemove = useCallback(
    (indexToRemove: number) => {
      const updated = productCard.filter((_, index) => index !== indexToRemove);
      updateProductCards(updated);
    },
    [productCard, updateProductCards]
  );

  const onAdd = useCallback(() => {
    const index = productCard.length;
    const defaultCard = defaultWidgetMakerConfig.productCard[0];

    const newCard: ProductCardConfig = {
      ...defaultCard,
      field: '',
      position: index,
    };

    updateProductCards([...productCard, newCard]);
  }, [dataFields, productCard, updateProductCards]);

  const onMoveCard = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (
        (direction === 'up' && index === 0) ||
        (direction === 'down' && index === productCard.length - 1)
      ) {
        return;
      }

      const newOrder = [...productCard];
      const [movedCard] = newOrder.splice(index, 1);
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      newOrder.splice(newIndex, 0, movedCard);

      updateProductCards(newOrder);
    },
    [productCard, updateProductCards]
  );

  const isEmpty = !dataFieldsToDisplay.length;

  return {
    dataFieldsToDisplay,
    dataFieldsDropdownOptions,
    onMoveCard,
    handleSelectChange,
    handleStyleChange,
    onRemove,
    onAdd,
    isEmpty,
  };
};
